import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useComunePhoto } from './useComunePhoto';

type PhotoHeroProps = {
  comuneName: string;
};

/**
 * F5.5 — Wikipedia hero image, shown at the top of DetailsPanel.
 *
 * - Lazy <img>, async decoding, fixed aspect ratio.
 * - Quiet Atlas paper-2 background as a loading/error fallback.
 * - Renders nothing when the photo is unresolved or fails to load.
 */
export function PhotoHero({ comuneName }: PhotoHeroProps) {
  const { t } = useTranslation();
  const { data: photo } = useComunePhoto(comuneName);
  const [errored, setErrored] = useState(false);

  if (!photo || errored) return null;

  return (
    <div className="pt-2">
      <div
        className="overflow-hidden rounded-md"
        style={{ background: 'var(--color-paper-2)' }}
      >
        <img
          src={photo.url}
          alt={comuneName}
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
          className="aspect-[4/3] w-full object-cover"
        />
      </div>
      <a
        href={photo.pageUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="qa-meta mt-1 inline-block hover:underline"
        style={{ color: 'var(--color-ink-500)' }}
        title={t('details.photo.openOnWikipedia', {
          defaultValue: 'Open on Wikipedia',
        })}
      >
        {t('details.photo.source', { defaultValue: 'Wikipedia · italiano' })}
      </a>
    </div>
  );
}
